/**
 * =====================================================
 * RESERVATION APPLICATION
 * 4-Step Flow with Embedded Menu Selection
 * =====================================================
 * Step 1: Guest Details
 * Step 2: Date & Time
 * Step 3: Menu Selection
 * Step 4: Summary & Payment
 */

const ReservationApp = (function() {
  'use strict';

  // ============== STATE ==============
  let currentStep = 1;
  let selectedTimeSlot = null;
  let menuData = null;
  let cart = [];
  let activeCategory = 'all';
  let activeFilter = 'all';
  let searchQuery = '';
  let stripe = null;
  let cardElement = null;

  // ============== CONFIG ==============
  const config = window.ReservationConfig || {};

  // ============== INITIALIZATION ==============

  function init() {
    console.log('ReservationApp: Initializing...');

    setupDateInput();
    setupEventListeners();

    // Initialize Stripe if needed
    if (config.showPayment && config.stripe?.publishableKey) {
      initStripe();
    }

    console.log('ReservationApp: Ready');
  }

  function setupEventListeners() {
    // Date input
    const dateInput = document.getElementById('reservation-date');
    if (dateInput) {
      dateInput.addEventListener('change', handleDateChange);
    }

    // Menu search
    const searchInput = document.getElementById('menu-search');
    if (searchInput) {
      searchInput.addEventListener('input', debounce(handleSearch, 300));
    }

    // Search clear
    const searchClear = document.getElementById('search-clear');
    if (searchClear) {
      searchClear.addEventListener('click', clearSearch);
    }

    // Dietary filters
    document.querySelectorAll('.filter-tag').forEach(tag => {
      tag.addEventListener('click', () => handleFilterClick(tag));
    });
  }

  function setupDateInput() {
    const dateInput = document.getElementById('reservation-date');
    if (!dateInput) return;

    const now = new Date();
    const minDate = new Date(now.getTime() + (config.advanceHours || 24) * 60 * 60 * 1000);
    const maxDate = new Date(now.getTime() + (config.maxAdvanceDays || 30) * 24 * 60 * 60 * 1000);

    dateInput.min = formatDateForInput(minDate);
    dateInput.max = formatDateForInput(maxDate);
  }


  // ============== STEP NAVIGATION ==============

  function goToStep(step) {
    // Validate before moving forward
    if (step > currentStep) {
      if (!validateCurrentStep()) {
        return;
      }
    }

    // Special actions when entering a step
    if (step === 3 && !menuData) {
      loadMenu();
    }

    if (step === 4) {
      populateSummary();
    }

    // Hide all steps
    document.querySelectorAll('.form-step').forEach(el => {
      el.style.display = 'none';
    });

    // Show target step
    const targetStep = document.querySelector(`[data-form-step="${step}"]`);
    if (targetStep) {
      targetStep.style.display = 'block';
    }

    // Update step indicators
    document.querySelectorAll('.step').forEach(el => {
      const stepNum = parseInt(el.dataset.step);
      el.classList.remove('active', 'completed');

      if (stepNum < step) {
        el.classList.add('completed');
      } else if (stepNum === step) {
        el.classList.add('active');
      }
    });

    // Update step lines
    document.querySelectorAll('.step-line').forEach(el => {
      const lineNum = parseInt(el.dataset.line);
      el.classList.toggle('completed', lineNum < step);
    });

    currentStep = step;
    hideMessage();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function validateCurrentStep() {
    if (currentStep === 1) {
      return validateStep1();
    } else if (currentStep === 2) {
      return validateStep2();
    } else if (currentStep === 3) {
      return validateStep3();
    }
    return true;
  }

  function validateStep1() {
    const name = document.getElementById('reservation-name')?.value.trim();
    const email = document.getElementById('reservation-email')?.value.trim();
    const phone = document.getElementById('reservation-phone')?.value.trim();
    const guests = document.getElementById('reservation-guests')?.value;

    if (!name || !email || !phone || !guests) {
      showMessage(config.messages?.fillRequired || 'Please fill in all required fields', 'error');
      return false;
    }

    if (!isValidEmail(email)) {
      showMessage(config.messages?.invalidEmail || 'Please enter a valid email address', 'error');
      return false;
    }

    return true;
  }

  function validateStep2() {
    if (!selectedTimeSlot) {
      showMessage(config.messages?.selectTime || 'Please select a time slot', 'error');
      return false;
    }
    return true;
  }

  function validateStep3() {
    if (cart.length === 0) {
      showMessage(config.messages?.emptyCart || 'Please add at least one item to your order', 'error');
      return false;
    }
    return true;
  }


  // ============== STEP 2: DATE & TIME ==============

  function handleDateChange(e) {
    const date = e.target.value;

    if (!date) {
      renderTimeSlotsPlaceholder();
      return;
    }

    loadTimeSlots(date);
  }

  function loadTimeSlots(date) {
    const container = document.getElementById('time-slots-container');
    const loading = document.getElementById('slots-loading');
    const continueBtn = document.getElementById('continue-to-menu-btn');

    container.innerHTML = '';
    loading.style.display = 'flex';
    continueBtn.disabled = true;
    selectedTimeSlot = null;

    // Try to fetch from API, fallback to default slots
    if (config.reservationApiUrl) {
      fetch(`${config.reservationApiUrl}?action=getAvailability&date=${date}`)
        .then(res => res.json())
        .then(data => {
          loading.style.display = 'none';
          if (data.success && data.slots) {
            renderTimeSlots(data.slots, date);
          } else {
            renderDefaultTimeSlots(date);
          }
        })
        .catch(() => {
          loading.style.display = 'none';
          renderDefaultTimeSlots(date);
        });
    } else {
      loading.style.display = 'none';
      renderDefaultTimeSlots(date);
    }
  }

  function renderTimeSlots(slots, date) {
    const container = document.getElementById('time-slots-container');
    container.innerHTML = '';

    slots.forEach(slot => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'time-slot-btn';
      btn.dataset.time = slot.time;
      btn.dataset.date = date;
      btn.innerHTML = `<span class="slot-time">${formatTime(slot.time)}</span>`;

      if (slot.available === 0) {
        btn.disabled = true;
        btn.classList.add('unavailable');
      } else {
        btn.addEventListener('click', () => selectTimeSlot(btn));
      }

      container.appendChild(btn);
    });
  }

  function renderDefaultTimeSlots(date) {
    const container = document.getElementById('time-slots-container');
    container.innerHTML = '';

    const timeSlots = config.timeSlots || ['18:00', '18:30', '19:00', '19:30', '20:00', '20:30'];

    timeSlots.forEach(time => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'time-slot-btn';
      btn.dataset.time = time.trim();
      btn.dataset.date = date;
      btn.innerHTML = `<span class="slot-time">${formatTime(time.trim())}</span>`;
      btn.addEventListener('click', () => selectTimeSlot(btn));
      container.appendChild(btn);
    });
  }

  function renderTimeSlotsPlaceholder() {
    const container = document.getElementById('time-slots-container');
    container.innerHTML = '<p class="time-slots-placeholder">Please select a date first</p>';
    selectedTimeSlot = null;
    document.getElementById('continue-to-menu-btn').disabled = true;
  }

  function selectTimeSlot(btn) {
    document.querySelectorAll('.time-slot-btn').forEach(b => b.classList.remove('selected'));
    btn.classList.add('selected');

    selectedTimeSlot = {
      time: btn.dataset.time,
      date: btn.dataset.date
    };

    document.getElementById('continue-to-menu-btn').disabled = false;
  }


  // ============== STEP 3: MENU SELECTION ==============

  async function loadMenu() {
    const loading = document.getElementById('menu-loading');
    const errorEl = document.getElementById('menu-error');
    const grid = document.getElementById('menu-items-grid');

    loading.style.display = 'flex';
    errorEl.style.display = 'none';
    grid.style.display = 'none';

    try {
      const response = await fetch(`${config.menuApiUrl}?action=getMenu`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load menu');
      }

      menuData = data.data;
      console.log('Menu loaded:', menuData);

      renderCategories();
      renderMenuItems();

      loading.style.display = 'none';
      grid.style.display = 'grid';

    } catch (error) {
      console.error('Error loading menu:', error);
      loading.style.display = 'none';
      errorEl.style.display = 'flex';
      document.getElementById('menu-error-text').textContent = error.message;
    }
  }

  function renderCategories() {
    const container = document.getElementById('category-tabs');
    if (!container || !menuData?.categories) return;

    const inner = container.querySelector('.category-tabs-inner');
    inner.innerHTML = '';

    // All tab
    const allTab = createCategoryTab({ category: 'all', display_name: 'All', icon: '🍽️' });
    allTab.classList.add('active');
    inner.appendChild(allTab);

    // Category tabs
    menuData.categories.forEach(cat => {
      inner.appendChild(createCategoryTab(cat));
    });
  }

  function createCategoryTab(cat) {
    const template = document.getElementById('category-tab-template');
    const clone = template.content.cloneNode(true);
    const btn = clone.querySelector('.category-tab');

    btn.dataset.category = cat.category;
    btn.querySelector('.category-icon').textContent = cat.icon || '🍴';
    btn.querySelector('.category-name').textContent = cat.display_name || cat.category;

    btn.addEventListener('click', () => {
      document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
      btn.classList.add('active');
      activeCategory = cat.category;
      renderMenuItems();
    });

    return btn;
  }

  function renderMenuItems() {
    const grid = document.getElementById('menu-items-grid');
    const noResults = document.getElementById('no-results');

    if (!grid || !menuData?.allItems) return;

    grid.innerHTML = '';

    let items = getFilteredItems();

    if (items.length === 0) {
      grid.style.display = 'none';
      noResults.style.display = 'flex';
      return;
    }

    grid.style.display = 'grid';
    noResults.style.display = 'none';

    items.forEach(item => {
      grid.appendChild(createMenuItemCard(item));
    });

    updateCartIndicators();
  }

  function getFilteredItems() {
    if (!menuData?.allItems) return [];

    let items = [...menuData.allItems];

    // Category filter
    if (activeCategory !== 'all') {
      items = items.filter(i => i.category === activeCategory);
    }

    // Dietary filter
    if (activeFilter !== 'all') {
      items = items.filter(i => i.dietary_tags?.includes(activeFilter));
    }

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        i.name.toLowerCase().includes(q) ||
        (i.description && i.description.toLowerCase().includes(q))
      );
    }

    return items;
  }

  function createMenuItemCard(item) {
    const template = document.getElementById('menu-item-template');
    const clone = template.content.cloneNode(true);
    const card = clone.querySelector('.menu-item-card');

    card.dataset.itemId = item.id;
    card.dataset.category = item.category;
    card.dataset.tags = (item.dietary_tags || []).join(',');

    // Image
    const img = card.querySelector('.menu-item-image img');
    const placeholder = card.querySelector('.menu-item-placeholder');

    if (item.image_url) {
      img.src = item.image_url;
      img.alt = item.name;
      img.onerror = () => {
        img.style.display = 'none';
        placeholder.style.display = 'flex';
      };
      placeholder.style.display = 'none';
    } else {
      img.style.display = 'none';
      placeholder.style.display = 'flex';
    }

    // Content
    card.querySelector('.menu-item-name').textContent = item.name;
    card.querySelector('.menu-item-price').textContent = formatCurrency(item.price);
    card.querySelector('.menu-item-description').textContent = item.description || '';

    // Tags
    const tagsEl = card.querySelector('.menu-item-tags');
    if (item.dietary_tags?.length) {
      tagsEl.innerHTML = item.dietary_tags.map(tag =>
        `<span class="tag tag-${tag}">${formatTag(tag)}</span>`
      ).join('');
    }

    // Spice
    const spiceEl = card.querySelector('.menu-item-spice');
    if (item.spice_level) {
      spiceEl.innerHTML = `<span class="spice">${formatSpice(item.spice_level)}</span>`;
    }

    return card;
  }

  function handleFilterClick(btn) {
    const isActive = btn.classList.contains('active');

    document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));

    if (!isActive) {
      btn.classList.add('active');
      activeFilter = btn.dataset.filter;
    } else {
      document.querySelector('.filter-tag[data-filter="all"]').classList.add('active');
      activeFilter = 'all';
    }

    renderMenuItems();
  }

  function handleSearch(e) {
    searchQuery = e.target.value.trim();
    document.getElementById('search-clear').style.display = searchQuery ? 'block' : 'none';
    renderMenuItems();
  }

  function clearSearch() {
    document.getElementById('menu-search').value = '';
    searchQuery = '';
    document.getElementById('search-clear').style.display = 'none';
    renderMenuItems();
  }

  function clearFilters() {
    activeCategory = 'all';
    activeFilter = 'all';
    searchQuery = '';

    document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
    document.querySelector('.category-tab[data-category="all"]')?.classList.add('active');

    document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));
    document.querySelector('.filter-tag[data-filter="all"]')?.classList.add('active');

    document.getElementById('menu-search').value = '';
    document.getElementById('search-clear').style.display = 'none';

    renderMenuItems();
  }


  // ============== CART MANAGEMENT ==============

  function addToCart(btn) {
    const card = btn.closest('.menu-item-card');
    const itemId = card.dataset.itemId;
    const item = menuData.allItems.find(i => i.id === itemId);

    if (!item) return;

    const existing = cart.find(c => c.id === itemId);

    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1
      });
    }

    updateCartUI();
    updateCartIndicators();
  }

  function updateCartItem(btn, delta) {
    const card = btn.closest('.menu-item-card');
    const itemId = card.dataset.itemId;

    const item = cart.find(c => c.id === itemId);
    if (!item) return;

    item.quantity += delta;

    if (item.quantity <= 0) {
      cart = cart.filter(c => c.id !== itemId);
    }

    updateCartUI();
    updateCartIndicators();
  }

  function updateCartItemFromCart(btn, delta) {
    const cartItem = btn.closest('.cart-item');
    const itemId = cartItem.dataset.itemId;

    const item = cart.find(c => c.id === itemId);
    if (!item) return;

    item.quantity += delta;

    if (item.quantity <= 0) {
      cart = cart.filter(c => c.id !== itemId);
    }

    updateCartUI();
    updateCartIndicators();
  }

  function removeFromCart(btn) {
    const cartItem = btn.closest('.cart-item');
    const itemId = cartItem.dataset.itemId;

    cart = cart.filter(c => c.id !== itemId);

    updateCartUI();
    updateCartIndicators();
  }

  function clearCart() {
    if (!confirm('Clear all items from your cart?')) return;
    cart = [];
    updateCartUI();
    updateCartIndicators();
  }

  function getCartTotals() {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    const taxPercent = parseFloat(config.taxPercent) || 0;
    const gratuityPercent = parseFloat(config.gratuityPercent) || 0;

    const tax = Math.round(subtotal * (taxPercent / 100));
    const gratuity = Math.round(subtotal * (gratuityPercent / 100));
    const total = subtotal + tax + gratuity;

    return { subtotal, tax, gratuity, total, itemCount };
  }

  function updateCartUI() {
    const totals = getCartTotals();
    const isEmpty = cart.length === 0;

    // Counts
    document.getElementById('cart-count').textContent = totals.itemCount;
    document.getElementById('mobile-cart-count').textContent = totals.itemCount;
    document.getElementById('mobile-cart-total').textContent = formatCurrency(totals.total);

    // Toggle visibility
    document.getElementById('cart-empty').style.display = isEmpty ? 'block' : 'none';
    document.getElementById('cart-items').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('cart-summary').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('clear-cart-btn').style.display = isEmpty ? 'none' : 'block';

    // Update totals
    document.getElementById('cart-subtotal').textContent = formatCurrency(totals.subtotal);
    document.getElementById('cart-tax').textContent = formatCurrency(totals.tax);
    document.getElementById('cart-gratuity').textContent = formatCurrency(totals.gratuity);
    document.getElementById('cart-total').textContent = formatCurrency(totals.total);

    // Render cart items
    renderCartItems();

    // Enable/disable continue button
    document.getElementById('continue-to-payment-btn').disabled = isEmpty;
  }

  function renderCartItems() {
    const container = document.getElementById('cart-items');
    container.innerHTML = '';

    cart.forEach(item => {
      const template = document.getElementById('cart-item-template');
      const clone = template.content.cloneNode(true);
      const cartItem = clone.querySelector('.cart-item');

      cartItem.dataset.itemId = item.id;
      cartItem.querySelector('.cart-item-name').textContent = item.name;
      cartItem.querySelector('.cart-item-price').textContent = formatCurrency(item.price * item.quantity);
      cartItem.querySelector('.cart-item-qty').textContent = item.quantity;

      container.appendChild(cartItem);
    });
  }

  function updateCartIndicators() {
    document.querySelectorAll('.menu-item-card').forEach(card => {
      const itemId = card.dataset.itemId;
      const cartItem = cart.find(c => c.id === itemId);

      const actions = card.querySelector('.menu-item-actions');
      const inCart = card.querySelector('.menu-item-in-cart');

      if (cartItem) {
        actions.style.display = 'none';
        inCart.style.display = 'flex';
        inCart.querySelector('.in-cart-qty').textContent = cartItem.quantity;
        card.classList.add('in-cart');
      } else {
        actions.style.display = 'flex';
        inCart.style.display = 'none';
        card.classList.remove('in-cart');
      }
    });
  }

  function toggleMobileCart() {
    const sidebar = document.getElementById('cart-sidebar');
    sidebar.classList.toggle('mobile-open');
    document.body.classList.toggle('cart-open');
  }


  // ============== STEP 4: SUMMARY & PAYMENT ==============

  function populateSummary() {
    // Guest info
    document.getElementById('summary-name').textContent = document.getElementById('reservation-name')?.value || '';
    document.getElementById('summary-email').textContent = document.getElementById('reservation-email')?.value || '';
    document.getElementById('summary-phone').textContent = document.getElementById('reservation-phone')?.value || '';
    document.getElementById('summary-guests').textContent = (document.getElementById('reservation-guests')?.value || '0') + ' guests';

    // Date & Time
    if (selectedTimeSlot) {
      document.getElementById('summary-date').textContent = formatDateDisplay(selectedTimeSlot.date);
      document.getElementById('summary-time').textContent = formatTime(selectedTimeSlot.time);
    }

    // Order items
    const orderContainer = document.getElementById('summary-order-items');
    orderContainer.innerHTML = '';

    cart.forEach(item => {
      const div = document.createElement('div');
      div.className = 'summary-order-item';
      div.innerHTML = `
        <span class="summary-item-qty">${item.quantity}×</span>
        <span class="summary-item-name">${item.name}</span>
        <span class="summary-item-price">${formatCurrency(item.price * item.quantity)}</span>
      `;
      orderContainer.appendChild(div);
    });

    // Totals
    const totals = getCartTotals();
    document.getElementById('summary-subtotal').textContent = formatCurrency(totals.subtotal);
    document.getElementById('summary-tax').textContent = formatCurrency(totals.tax);
    document.getElementById('summary-gratuity').textContent = formatCurrency(totals.gratuity);
    document.getElementById('summary-total').textContent = formatCurrency(totals.total);
  }

  function initStripe() {
    if (!config.stripe?.publishableKey) return;

    try {
      stripe = Stripe(config.stripe.publishableKey);
      const elements = stripe.elements();

      cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#1a1a2e',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            '::placeholder': { color: '#7f8c8d' }
          },
          invalid: {
            color: '#e74c3c',
            iconColor: '#e74c3c'
          }
        }
      });

      cardElement.mount('#card-element');

      cardElement.on('change', (event) => {
        const errorEl = document.getElementById('card-errors');
        errorEl.textContent = event.error ? event.error.message : '';
      });

      console.log('Stripe initialized');
    } catch (error) {
      console.error('Stripe init error:', error);
    }
  }

  async function confirmReservation() {
    // Validate terms
    const terms = document.getElementById('accept-terms');
    if (terms && !terms.checked) {
      showMessage(config.messages?.acceptTerms || 'Please accept the terms and conditions', 'error');
      return;
    }

    const confirmBtn = document.getElementById('confirm-btn');
    confirmBtn.disabled = true;
    confirmBtn.innerHTML = '⏳ ' + (config.messages?.processing || 'Processing...');

    try {
      const totals = getCartTotals();

      const reservationData = {
        name: document.getElementById('reservation-name')?.value,
        email: document.getElementById('reservation-email')?.value,
        phone: document.getElementById('reservation-phone')?.value,
        guests: document.getElementById('reservation-guests')?.value,
        date: selectedTimeSlot?.date,
        time: selectedTimeSlot?.time,
        specialRequests: document.getElementById('reservation-requests')?.value || '',
        orderItems: cart,
        totals: totals
      };

      if (config.showPayment && stripe && cardElement) {
        await processPayment(reservationData, totals);
      } else {
        await submitReservation(reservationData);
      }

    } catch (error) {
      console.error('Error:', error);
      showMessage(error.message || config.messages?.error, 'error');

      confirmBtn.disabled = false;
      confirmBtn.innerHTML = '💳 ' + (config.pay_button || 'Pay & Confirm Reservation');
    }
  }

  async function processPayment(reservationData, totals) {
    // Create payment intent
    const response = await fetch(config.reservationApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'createPaymentIntent',
        amount: totals.total,
        currency: config.stripe?.currency || 'usd',
        reservation: reservationData
      })
    });

    const data = await response.json();

    if (!data.success || !data.clientSecret) {
      throw new Error(data.error || 'Failed to create payment');
    }

    // Confirm payment
    const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: reservationData.name,
          email: reservationData.email,
          phone: reservationData.phone
        }
      }
    });

    if (error) {
      throw new Error(error.message);
    }

    if (paymentIntent.status === 'succeeded') {
      reservationData.paymentIntentId = paymentIntent.id;
      reservationData.paymentStatus = 'paid';
      await submitReservation(reservationData);
    } else {
      throw new Error('Payment was not completed');
    }
  }

  async function submitReservation(reservationData) {
    const response = await fetch(config.reservationApiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'createReservation',
        reservation: reservationData
      })
    });

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to create reservation');
    }

    showSuccess(data.reservationId, reservationData);
  }

  function showSuccess(reservationId, data) {
    const totals = getCartTotals();

    document.getElementById('success-reservation-id').textContent = reservationId || 'PENDING';
    document.getElementById('success-date').textContent = formatDateDisplay(data.date);
    document.getElementById('success-time').textContent = formatTime(data.time);
    document.getElementById('success-guests').textContent = data.guests + ' guests';
    document.getElementById('success-amount').textContent = formatCurrency(totals.total);

    // Show success step
    document.querySelectorAll('.form-step').forEach(el => el.style.display = 'none');
    document.querySelector('[data-form-step="success"]').style.display = 'block';

    // All steps completed
    document.querySelectorAll('.step').forEach(el => el.classList.add('completed'));
    document.querySelectorAll('.step-line').forEach(el => el.classList.add('completed'));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }


  // ============== UTILITIES ==============

  function showMessage(text, type = 'info') {
    const el = document.getElementById('reservation-message');
    if (!el) return;

    el.textContent = text;
    el.className = `reservation-message ${type}`;
    el.style.display = 'block';
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideMessage() {
    const el = document.getElementById('reservation-message');
    if (el) el.style.display = 'none';
  }

  function formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config.stripe?.currency?.toUpperCase() || 'USD'
    }).format(cents / 100);
  }

  function formatDateForInput(date) {
    return date.toISOString().split('T')[0];
  }

  function formatDateDisplay(dateStr) {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  function formatTime(timeStr) {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  }

  function formatTag(tag) {
    const map = {
      'vegetarian': '🥬 Veg',
      'vegan': '🌱 Vegan',
      'gluten-free': '🌾 GF'
    };
    return map[tag] || tag;
  }

  function formatSpice(level) {
    const map = {
      'mild': '🌶️',
      'medium': '🌶️🌶️',
      'hot': '🌶️🌶️🌶️'
    };
    return map[level] || level;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }


  // ============== PUBLIC API ==============

  return {
    init,
    goToStep,
    loadMenu,
    addToCart,
    updateCartItem,
    updateCartItemFromCart,
    removeFromCart,
    clearCart,
    clearFilters,
    toggleMobileCart,
    confirmReservation
  };

})();

// Initialize
document.addEventListener('DOMContentLoaded', ReservationApp.init);